import { FileSearch, Search } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import type { SearchPrivateHiresResult } from "@/lib/coach-manager/client";
import type { PrivateHire } from "@/lib/coach-manager/types";
import { PrivateHireDetailDrawer } from "./PrivateHireDetailDrawer";
import { PrivateHiresTable } from "./PrivateHiresTable";

interface PrivateHireSearchResultsProps {
  result: SearchPrivateHiresResult | undefined;
  isPending: boolean;
  error: Error | null;
}

/**
 * Renders every state of a PrivateHire search: idle prompt, loading, a
 * route/transport-level error, a Coach-Manager-level failure (`ok: false`),
 * zero matches, or the matched PrivateHires as a matrix table — clicking a
 * row opens the full detail in a side drawer. Mirrors `ContractSearchResults`
 * exactly, minus the `buInstanceId` threading needed for Contract's
 * "Initiate Copy" action — PrivateHire search is display-only.
 */
export function PrivateHireSearchResults({
  result,
  isPending,
  error,
}: PrivateHireSearchResultsProps) {
  const { label } = useBuInstanceLabel();
  const [selectedPrivateHire, setSelectedPrivateHire] = useState<PrivateHire | null>(null);

  if (isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error.message} />;
  }

  if (!result) {
    return (
      <EmptyState
        icon={Search}
        title="Search for a private hire"
        description={`Pick a ${label} and enter search criteria above.`}
      />
    );
  }

  if (!result.ok) {
    return <ErrorState message={result.error} />;
  }

  if (result.privateHires.length === 0) {
    return (
      <EmptyState
        icon={FileSearch}
        title="No private hires found"
        description="Try different search criteria."
      />
    );
  }

  return (
    <>
      <PrivateHiresTable privateHires={result.privateHires} onSelect={setSelectedPrivateHire} />
      <PrivateHireDetailDrawer
        privateHire={selectedPrivateHire}
        onClose={() => setSelectedPrivateHire(null)}
      />
    </>
  );
}
